import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Divider,
    TextField,
    Autocomplete
} from '@mui/material';

const DB_NAME = 'FinancialToolDB';
const DB_VERSION = 1;
const STORE_NAMES = {
    FILES: 'files',
    HEADER_MAPPING: 'header_mapping'
};

// Helper functions for IndexedDB operations
const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            reject(`IndexedDB error: ${event.target.error}`);
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAMES.FILES)) {
                db.createObjectStore(STORE_NAMES.FILES, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORE_NAMES.HEADER_MAPPING)) {
                db.createObjectStore(STORE_NAMES.HEADER_MAPPING, { keyPath: 'id' });
            }
        };
    });
};

// DB helpers specific to HeaderMapping
const dbHelpers = {
    // Save HeaderMapping state to IndexedDB
    saveHeaderMappingState: async (state) => {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAMES.HEADER_MAPPING], 'readwrite');
            const store = transaction.objectStore(STORE_NAMES.HEADER_MAPPING);

            // Store with ID 1
            await store.put({
                id: 1,
                accountHeadMappings: state.accountHeadMappings,
                visibilitySettings: state.visibilitySettings,
                conflictHeaderSettings: state.conflictHeaderSettings,
                timestamp: new Date().getTime()
            });

            return true;
        } catch (error) {
            console.error('Error saving HeaderMapping state to IndexedDB:', error);
            return false;
        }
    },

    // Load HeaderMapping state from IndexedDB
    loadHeaderMappingState: async () => {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAMES.HEADER_MAPPING], 'readonly');
            const store = transaction.objectStore(STORE_NAMES.HEADER_MAPPING);

            return new Promise((resolve, reject) => {
                const request = store.get(1);

                request.onsuccess = () => {
                    if (request.result) {
                        resolve({
                            accountHeadMappings: request.result.accountHeadMappings,
                            visibilitySettings: request.result.visibilitySettings,
                            conflictHeaderSettings: request.result.conflictHeaderSettings
                        });
                    } else {
                        resolve(null);
                    }
                };

                request.onerror = (event) => {
                    reject(`Error loading HeaderMapping state: ${event.target.error}`);
                };
            });
        } catch (error) {
            console.error('Error accessing IndexedDB for HeaderMapping state:', error);
            return null;
        }
    },

    // Clear HeaderMapping state from IndexedDB
    clearHeaderMappingState: async () => {
        try {
            const db = await openDB();
            const transaction = db.transaction([STORE_NAMES.HEADER_MAPPING], 'readwrite');
            const store = transaction.objectStore(STORE_NAMES.HEADER_MAPPING);
            store.clear();
            return true;
        } catch (error) {
            console.error('Error clearing HeaderMapping state from IndexedDB:', error);
            return false;
        }
    }
};

// Categories for account head mapping
const CATEGORY_OPTIONS = [
    { value: 'sales', label: 'Sales Revenue' },
    { value: 'cogs', label: 'Cost of Goods Sold' },
    { value: 'gross_profit', label: 'Gross Profit' },
    { value: 'expense', label: 'Expense' },
    { value: 'income', label: 'Income' }
];

// Visibility options
const VISIBILITY_OPTIONS = [
    { value: 'show', label: 'Show' },
    { value: 'hide', label: 'Hide' }
];

// Format account head for display: trim and remove periods
const formatAccountHeadForDisplay = (head) => {
    if (!head) return '';
    const words = head.trim().replace(/\./g, '').split(" ");
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

// Function to remove duplicates while preserving original values for mapping
const removeDuplicateAccountHeads = (accountHeads) => {
    const uniqueHeads = [];
    const displayMap = new Map();

    accountHeads.forEach(head => {
        const formattedDisplay = formatAccountHeadForDisplay(head).toLowerCase();

        // If we haven't seen this formatted head before, add it to our results
        if (!displayMap.has(formattedDisplay)) {
            displayMap.set(formattedDisplay, head);
            uniqueHeads.push(head);
        }
    });

    return uniqueHeads;
};

const HeaderMapping = ({
    accountHeads,
    accountHeadCategories = {}, // Prop for pre-categorized account heads
    onMappingComplete,
    loading,
    error
}) => {
    // Initialize all state hooks at the top level
    const [accountHeadMappings, setAccountHeadMappings] = useState({});
    const [uniqueAccountHeads, setUniqueAccountHeads] = useState([]);
    const [originalValuesMap, setOriginalValuesMap] = useState({});
    const [visibilitySettings, setVisibilitySettings] = useState({});
    const [conflictHeaderSettings, setConflictHeaderSettings] = useState({});
    const [isLoadingState, setIsLoadingState] = useState(true);
    const [hasSavedState, setHasSavedState] = useState(false);

    // Calculate derived values using useMemo
    const mergedHeaders = useMemo(() => {
        const result = [];
        Object.values(conflictHeaderSettings).forEach(value => {
            if (value && !result.includes(value)) {
                result.push(value);
            }
        });
        return result;
    }, [conflictHeaderSettings]);

    const displayedAccountHeads = useMemo(() => {
        if (!uniqueAccountHeads || uniqueAccountHeads.length === 0) {
            return [];
        }
        return uniqueAccountHeads.filter(head => !mergedHeaders.includes(head));
    }, [uniqueAccountHeads, mergedHeaders]);

    useEffect(() => {
        const initializeComponent = async () => {
            if (accountHeads && accountHeads.length > 0) {
                setIsLoadingState(true);

                try {
                    // First try to load saved state from IndexedDB
                    const savedState = await dbHelpers.loadHeaderMappingState();

                    if (savedState) {
                        // We have saved state, but we need to check if it's compatible with current accountHeads
                        const formattedToOriginalMap = {};

                        // Process account heads to build the mapping
                        accountHeads.forEach(head => {
                            const formatted = formatAccountHeadForDisplay(head).toLowerCase();

                            if (!formattedToOriginalMap[formatted]) {
                                formattedToOriginalMap[formatted] = [];
                            }

                            formattedToOriginalMap[formatted].push(head);
                        });

                        // Get unique account heads for display
                        const uniqueHeads = removeDuplicateAccountHeads(accountHeads);
                        setUniqueAccountHeads(uniqueHeads);
                        setOriginalValuesMap(formattedToOriginalMap);

                        // Use saved mappings if they exist for current account heads
                        const hasValidMappings = Object.keys(savedState.accountHeadMappings)
                            .some(head => accountHeads.includes(head));

                        if (hasValidMappings) {
                            // Load the saved state
                            setAccountHeadMappings(savedState.accountHeadMappings);
                            setVisibilitySettings(savedState.visibilitySettings);
                            setConflictHeaderSettings(savedState.conflictHeaderSettings);
                            setHasSavedState(true);
                        } else {
                            // Saved state doesn't match current account heads, initialize fresh
                            initializeFreshState(accountHeads, accountHeadCategories, formattedToOriginalMap);
                        }
                    } else {
                        // No saved state, initialize fresh
                        const formattedToOriginalMap = {};

                        // Process account heads
                        accountHeads.forEach(head => {
                            const formatted = formatAccountHeadForDisplay(head).toLowerCase();

                            if (!formattedToOriginalMap[formatted]) {
                                formattedToOriginalMap[formatted] = [];
                            }

                            formattedToOriginalMap[formatted].push(head);
                        });

                        // Initialize with fresh data
                        initializeFreshState(accountHeads, accountHeadCategories, formattedToOriginalMap);
                    }
                } catch (error) {
                    console.error('Error during HeaderMapping initialization:', error);

                    // On error, initialize fresh
                    const formattedToOriginalMap = {};
                    accountHeads.forEach(head => {
                        const formatted = formatAccountHeadForDisplay(head).toLowerCase();
                        if (!formattedToOriginalMap[formatted]) {
                            formattedToOriginalMap[formatted] = [];
                        }
                        formattedToOriginalMap[formatted].push(head);
                    });

                    initializeFreshState(accountHeads, accountHeadCategories, formattedToOriginalMap);
                } finally {
                    setIsLoadingState(false);
                }
            }
        };

        initializeComponent();
    }, [accountHeads, accountHeadCategories]);

    const initializeFreshState = (heads, categories, formattedMap) => {
        // Get unique account heads for display
        const uniqueHeads = removeDuplicateAccountHeads(heads);
        setUniqueAccountHeads(uniqueHeads);

        // Store mapping from displayed values to all their original values
        setOriginalValuesMap(formattedMap);

        // Initialize mappings, visibility, and conflict headers for all original account heads
        const initialMappings = {};
        const initialVisibility = {};
        const initialConflictHeaders = {};

        heads.forEach(head => {
            // Use the pre-categorized value from the backend if available
            const category = categories[head] || 'expense';
            initialMappings[head] = category;
            initialVisibility[head] = 'show'; // Default to 'show'
            initialConflictHeaders[head] = ''; // Default to empty string (None)
        });

        setAccountHeadMappings(initialMappings);
        setVisibilitySettings(initialVisibility);
        setConflictHeaderSettings(initialConflictHeaders);
        setHasSavedState(false);
    };

    // Save state to IndexedDB whenever any of the mapping states change
    useEffect(() => {
        const saveState = async () => {
            // Only save if component is fully initialized and not in loading state
            if (!isLoadingState && Object.keys(accountHeadMappings).length > 0) {
                await dbHelpers.saveHeaderMappingState({
                    accountHeadMappings,
                    visibilitySettings,
                    conflictHeaderSettings
                });
            }
        };

        saveState();
    }, [accountHeadMappings, visibilitySettings, conflictHeaderSettings, isLoadingState]);

    useEffect(() => {
        const handleResetEvent = async () => {
            await dbHelpers.clearHeaderMappingState();
            setHasSavedState(false);

            // Re-initialize with fresh data
            if (accountHeads && accountHeads.length > 0) {
                const formattedToOriginalMap = {};
                accountHeads.forEach(head => {
                    const formatted = formatAccountHeadForDisplay(head).toLowerCase();
                    if (!formattedToOriginalMap[formatted]) {
                        formattedToOriginalMap[formatted] = [];
                    }
                    formattedToOriginalMap[formatted].push(head);
                });

                initializeFreshState(accountHeads, accountHeadCategories, formattedToOriginalMap);
            }
        };

        window.addEventListener('reset-financial-tool', handleResetEvent);

        return () => {
            window.removeEventListener('reset-financial-tool', handleResetEvent);
        };
    }, [accountHeads, accountHeadCategories]);

    const handleCategoryChange = (displayedHead, category) => {
        // Get the formatted version of the displayed head
        const formattedDisplay = formatAccountHeadForDisplay(displayedHead).toLowerCase();

        // Find all original values that correspond to this displayed value
        const originalValues = originalValuesMap[formattedDisplay] || [displayedHead];

        // Update mapping for all corresponding original values
        setAccountHeadMappings(prev => {
            const updated = { ...prev };
            originalValues.forEach(originalHead => {
                updated[originalHead] = category;
            });
            return updated;
        });
    };

    const handleVisibilityChange = (displayedHead, visibility) => {
        // Get the formatted version of the displayed head
        const formattedDisplay = formatAccountHeadForDisplay(displayedHead).toLowerCase();

        // Find all original values that correspond to this displayed value
        const originalValues = originalValuesMap[formattedDisplay] || [displayedHead];

        // Update visibility for all corresponding original values
        setVisibilitySettings(prev => {
            const updated = { ...prev };
            originalValues.forEach(originalHead => {
                updated[originalHead] = visibility;
            });
            return updated;
        });
    };

    // Handle conflict header change
    const handleConflictHeaderChange = (displayedHead, conflictHeader) => {
        // Get the formatted version of the displayed head
        const formattedDisplay = formatAccountHeadForDisplay(displayedHead).toLowerCase();

        // Find all original values that correspond to this displayed value
        const originalValues = originalValuesMap[formattedDisplay] || [displayedHead];

        // Update conflict header for all corresponding original values
        setConflictHeaderSettings(prev => {
            const updated = { ...prev };
            originalValues.forEach(originalHead => {
                updated[originalHead] = conflictHeader || ''; // Handle null case
            });
            return updated;
        });

        // If a conflict header is selected, set visibility to 'hide'
        if (conflictHeader) {
        } else {
            // If conflict header is cleared, reset visibility to 'show'
            setVisibilitySettings(prev => {
                const updated = { ...prev };
                originalValues.forEach(originalHead => {
                    updated[originalHead] = 'show';
                });
                return updated;
            });
        }
    };

    const handleSubmit = () => {
        if (onMappingComplete) {
            // Create the final mapping object
            // If visibility is 'hide', the final category is 'hide'
            // Otherwise use the selected category
            const finalMappings = {};

            Object.keys(accountHeadMappings).forEach(head => {
                const visibility = visibilitySettings[head] || 'show';
                const conflictHeader = conflictHeaderSettings[head] || '';

                // Include conflict header in the final mappings
                finalMappings[head] = {
                    category: visibility === 'hide' ? 'hide' : accountHeadMappings[head],
                    conflictHeader: conflictHeader
                };
            });

            onMappingComplete(finalMappings);
        }
    };

    // Loading and error handlers
    if (loading || isLoadingState) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading account heads...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {error}
            </Alert>
        );
    }

    if (!accountHeads || accountHeads.length === 0) {
        return (
            <Alert severity="warning" sx={{ mb: 3 }}>
                No account heads found in the uploaded files.
            </Alert>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 4,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.light',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                mb: 4
            }}
        >
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Categorize Account Heads
            </Typography>

            <Divider sx={{ my: 2 }} />

            {hasSavedState && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Your previous categorization has been restored. You can continue where you left off.
                </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Note: When an account head is selected as "Merged To" for another head, it will be removed from this list.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Account Head</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Visibility</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Merged To</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedAccountHeads.map((head, index) => {
                            // Rest of the render logic remains the same
                            const displayHead = formatAccountHeadForDisplay(head);
                            const formattedLower = displayHead.toLowerCase();
                            const originalValues = originalValuesMap[formattedLower] || [head];
                            const firstOriginal = originalValues[0];

                            const otherHeaders = uniqueAccountHeads
                                .filter(otherHead => otherHead !== head && !mergedHeaders.includes(otherHead))
                                .map(otherHead => ({
                                    value: otherHead,
                                    label: formatAccountHeadForDisplay(otherHead)
                                }));

                            const currentConflictHeader = conflictHeaderSettings[firstOriginal] || '';
                            const selectedConflictHeaderOption = currentConflictHeader
                                ? { value: currentConflictHeader, label: formatAccountHeadForDisplay(currentConflictHeader) }
                                : null;

                            return (
                                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }}>
                                    <TableCell>
                                        {displayHead}
                                        {originalValues.length > 1 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                ({originalValues.length} identical entries)
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Visibility</InputLabel>
                                            <Select
                                                value={visibilitySettings[firstOriginal] || 'show'}
                                                onChange={(e) => handleVisibilityChange(head, e.target.value)}
                                                label="Visibility"
                                                disabled={!!conflictHeaderSettings[firstOriginal]}
                                            >
                                                {VISIBILITY_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                value={accountHeadMappings[firstOriginal] || 'expense'}
                                                onChange={(e) => handleCategoryChange(head, e.target.value)}
                                                label="Category"
                                                disabled={visibilitySettings[firstOriginal] === 'hide'}
                                            >
                                                {CATEGORY_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            size="small"
                                            options={[{ value: '', label: 'None' }, ...otherHeaders]}
                                            getOptionLabel={(option) => option.label || ""}
                                            value={selectedConflictHeaderOption || { value: '', label: 'None' }}
                                            onChange={(event, newValue) => {
                                                handleConflictHeaderChange(
                                                    head,
                                                    newValue && newValue.value !== '' ? newValue.value : ''
                                                );
                                            }}
                                            disabled={visibilitySettings[firstOriginal] === 'hide' && !conflictHeaderSettings[firstOriginal]}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Merged To"
                                                    variant="outlined"
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) =>
                                                option.value === value?.value
                                            }
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            renderOption={(props, option) => (
                                                <li {...props}>
                                                    {option.label}
                                                </li>
                                            )}
                                            noOptionsText="No matching headers"
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {mergedHeaders.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {mergedHeaders.length} account head(s) are hidden because they're merged with other headers.
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => onMappingComplete(null)}
                >
                    Back to Upload
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    Continue to SDE Selection
                </Button>
            </Box>
        </Paper>
    );
};

export default HeaderMapping;